// PST-2/PST-3/PST-4: autosave controller + project CRUD on top of a PersistDriver.
// ARC-3a: io imports core/doc only — thumbnail rendering and view state come in via hooks
// (wired by the composition root, which may use the engine).
import { createEmptyDoc, defaultActiveLayerId } from "../doc/document";
import type { Doc } from "../doc/entities";
import { fromJSON, toJSON, type LoadResult } from "../doc/serialize";
import type { DocStore } from "../doc/store";
import type { HistoryTail, PersistDriver, ProjectMeta, ViewState } from "./driver";
import { exportJSON, exportTracecad, importJSON, importTracecad } from "./projectFile";

export type SaveStatus = "idle" | "saving" | "saved" | "error";

const DEBOUNCE_MS = 500;
const MAX_WAIT_MS = 5000;
const THUMBNAIL_INTERVAL_MS = 30_000;
const BASE_PPMM = 96 / 25.4; // home-view scale (mirrors engine/camera BASE_PPMM)

export interface PersistHooks {
  getViewState(): ViewState;
  applyViewState(vs: ViewState): void;
  onStatus(status: SaveStatus): void;
  /** Renders a ~256 px preview of the doc; null = skip (cosmetic only, PST-2). */
  makeThumbnail(doc: Doc): Promise<Blob | null>;
}

export class Persistence {
  currentProjectId: string;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private firstPendingAt = 0;
  private lastThumbAt = 0;
  private dirty = false;

  constructor(
    private driver: PersistDriver,
    private store: DocStore,
    private hooks: PersistHooks
  ) {
    this.currentProjectId = store.doc.id;
  }

  /** PST-2: subscribe to the command stream; debounce 500 ms, max-wait 5 s. */
  start(): void {
    this.store.subscribe((_doc, cmd) => {
      if (!cmd) return; // replace() saves explicitly
      this.dirty = true;
      const now = Date.now();
      if (this.firstPendingAt === 0) this.firstPendingAt = now;
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      const overdue = now - this.firstPendingAt >= MAX_WAIT_MS;
      this.debounceTimer = setTimeout(() => void this.flush(), overdue ? 0 : DEBOUNCE_MS);
    });
    if (typeof window !== "undefined") {
      window.addEventListener("beforeunload", (e) => {
        if (this.dirty) {
          void this.flush(); // best-effort; IDB writes usually survive unload
          e.preventDefault();
        }
      });
    }
  }

  /** Save now, changed or not (a drawing repaired as it opened). */
  async saveNow(): Promise<void> {
    this.dirty = true;
    await this.flush();
  }

  async flush(): Promise<void> {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.firstPendingAt = 0;
    if (!this.dirty) return;
    this.dirty = false;
    this.hooks.onStatus("saving");
    try {
      const doc = this.store.doc;
      const json = toJSON(doc);
      const sizeBytes = JSON.stringify(json).length;
      await this.driver.putDoc(this.currentProjectId, json);
      await this.driver.putHistory(this.currentProjectId, this.store.historyTail());
      const prev = await this.driver.getMeta(this.currentProjectId);
      const meta: ProjectMeta = {
        id: this.currentProjectId,
        name: doc.name,
        createdAt: prev?.createdAt ?? doc.createdAt,
        modifiedAt: doc.modifiedAt,
        sizeBytes,
        thumbnail: prev?.thumbnail,
        viewState: this.hooks.getViewState()
      };
      if (Date.now() - this.lastThumbAt > THUMBNAIL_INTERVAL_MS) {
        const thumb = await this.hooks.makeThumbnail(doc);
        if (thumb) meta.thumbnail = thumb;
        this.lastThumbAt = Date.now();
      }
      await this.driver.putMeta(meta);
      await this.driver.putSetting("lastProjectId", this.currentProjectId);
      this.hooks.onStatus("saved");
    } catch (err) {
      // EC-ARC-8: never silently drop a save — stay dirty, retry on next command
      console.error("tracecad: autosave failed", err);
      this.dirty = true;
      this.hooks.onStatus("error");
    }
  }

  // ---- projects (PST-3) ------------------------------------------------------

  async listProjects(): Promise<ProjectMeta[]> {
    const all = await this.driver.listMeta();
    return all.sort((a, b) => b.modifiedAt.localeCompare(a.modifiedAt));
  }

  /** Read any saved project's document WITHOUT switching to it (Eden sync-all). */
  async readDoc(id: string): Promise<Doc | null> {
    if (id === this.currentProjectId) return this.store.doc;
    const json = await this.driver.getDoc(id);
    if (json === undefined) return null;
    const result = fromJSON(json);
    return result.ok ? result.doc : null;
  }

  /** Open by id. Returns errors instead of a partially-loaded doc (EC-PST-3). */
  async openProject(id: string): Promise<LoadResult> {
    const json = await this.driver.getDoc(id);
    if (json === undefined) return { ok: false, errors: ["Project data is missing."] };
    const result = fromJSON(json);
    if (!result.ok) return result;
    await this.flush(); // persist the outgoing project first
    this.currentProjectId = id;
    // A repaired drawing starts with no undo steps (they name parts the repair replaced), and is saved at once.
    const tail = result.repaired?.length ? undefined : await this.driver.getHistory(id);
    this.store.replace(result.doc, tail);
    const meta = await this.driver.getMeta(id);
    if (meta?.viewState) this.hooks.applyViewState(meta.viewState);
    await this.driver.putSetting("lastProjectId", id);
    if (result.repaired?.length) await this.saveNow();
    this.switched();
    return result;
  }

  async newProject(name = "Untitled"): Promise<void> {
    await this.flush();
    const doc = createEmptyDoc(name);
    this.currentProjectId = doc.id;
    this.store.replace(doc);
    this.hooks.applyViewState({
      camera: { cx: 0, cy: 0, scale: BASE_PPMM },
      activeLayerId: defaultActiveLayerId(doc)
    });
    this.dirty = true;
    await this.flush();
    this.switched();
  }

  /** Adopt an imported document as a NEW project (EXP-6: import never merges). */
  async adoptDoc(doc: Doc): Promise<void> {
    await this.flush();
    doc.id = crypto.randomUUID(); // never collide with an existing project
    this.currentProjectId = doc.id;
    this.store.replace(doc);
    this.dirty = true;
    await this.flush();
  }

  async duplicateProject(id: string): Promise<void> {
    const json = await this.driver.getDoc(id);
    const meta = await this.driver.getMeta(id);
    if (json === undefined || !meta) return;
    const copy = structuredClone(json) as Doc;
    copy.id = crypto.randomUUID();
    copy.name = `${meta.name} copy`;
    await this.driver.putDoc(copy.id, copy);
    await this.driver.putMeta({ ...meta, id: copy.id, name: copy.name, thumbnail: meta.thumbnail });
  }

  async renameProject(id: string, name: string): Promise<void> {
    const meta = await this.driver.getMeta(id);
    if (!meta) return;
    await this.driver.putMeta({ ...meta, name });
    if (id === this.currentProjectId) {
      this.store.dispatch({ kind: "setDocProps", before: { name: this.store.doc.name }, after: { name } });
    } else {
      const json = (await this.driver.getDoc(id)) as Doc | undefined;
      if (json) {
        json.name = name;
        await this.driver.putDoc(id, json);
      }
    }
  }

  /** Delete + sweep assets referenced by no remaining project (EC-PST-1: sweep is best-effort). */
  async deleteProject(id: string): Promise<void> {
    await this.driver.deleteDoc(id);
    await this.driver.deleteMeta(id);
    await this.driver.deleteHistory(id);
    try {
      const referenced = new Set<string>();
      for (const meta of await this.driver.listMeta()) {
        const json = (await this.driver.getDoc(meta.id)) as Doc | undefined;
        if (json) for (const hash of Object.keys(json.assets ?? {})) referenced.add(hash);
      }
      if (this.currentProjectId !== id) {
        for (const hash of Object.keys(this.store.doc.assets)) referenced.add(hash);
      }
      for (const hash of await this.driver.listAssetHashes()) {
        if (!referenced.has(hash)) await this.driver.deleteAsset(hash);
      }
    } catch (err) {
      console.warn("tracecad: asset sweep failed, retrying on next delete (EC-PST-1)", err);
    }
  }

  get isPersistent(): boolean {
    return this.driver.persistent;
  }

  /** Called after open/new/adopt switch the active project (EC-ARC-7 channel re-arm). */
  onProjectSwitch: ((id: string) => void) | null = null;

  private switched(): void {
    this.onProjectSwitch?.(this.currentProjectId);
  }

  // ---- files (EXP-6 / PST-6) --------------------------------------------------

  async exportFileBlob(): Promise<Blob> {
    await this.flush();
    return exportTracecad(this.store.doc, this.driver);
  }

  exportJsonBlob(): Blob {
    return exportJSON(this.store.doc);
  }

  /** Import .tracecad or .json as a NEW project. Returns error strings, or null on success. */
  async importFile(file: File): Promise<string[] | null> {
    if (file.name.toLowerCase().endsWith(".tracecad") || file.type.includes("zip")) {
      const { result, assets } = await importTracecad(new Uint8Array(await file.arrayBuffer()));
      if (!result.ok) return result.errors;
      for (const [hash, bytes] of assets) {
        await this.driver.putAsset(hash, new Blob([bytes.slice().buffer as ArrayBuffer]));
      }
      await this.adoptDoc(result.doc);
      this.switched();
      return null;
    }
    const result = importJSON(await file.text());
    if (!result.ok) return result.errors;
    await this.adoptDoc(result.doc);
    this.switched();
    return null;
  }

  /** Camera-only changes don't dispatch commands; persist the view state lazily. */
  private viewTimer: ReturnType<typeof setTimeout> | null = null;

  saveViewStateSoon(): void {
    if (this.viewTimer) clearTimeout(this.viewTimer);
    this.viewTimer = setTimeout(() => {
      void (async () => {
        const meta = await this.driver.getMeta(this.currentProjectId);
        if (meta) await this.driver.putMeta({ ...meta, viewState: this.hooks.getViewState() });
      })().catch(() => undefined);
    }, 1000);
  }
}

export interface BootstrapResult {
  doc: Doc;
  projectId: string;
  tail?: HistoryTail;
  viewState?: ViewState;
  loadError?: string;
  /** Sentences saying what the load repaired (src/doc/serialize.ts). The repaired drawing must be saved. */
  repaired?: string[];
}

/** PST-3 startup: reopen the last project (with history, PST-4), else newest, else fresh.
 *  E5: a deep-linked `overrideId` (from `?edenProject=`) takes precedence when it exists
 *  locally; if it isn't on this device the caller falls back to an Eden pull. */
export async function bootstrapDoc(driver: PersistDriver, overrideId?: string): Promise<BootstrapResult> {
  const lastId = await driver.getSetting<string>("lastProjectId");
  const tryLoad = async (id: string | undefined): Promise<BootstrapResult | { error: string } | null> => {
    if (!id) return null;
    const json = await driver.getDoc(id);
    if (json === undefined) return null;
    const result = fromJSON(json);
    if (!result.ok) return { error: result.errors.join("; ") };
    const meta = await driver.getMeta(id);
    // A repaired drawing starts with no undo steps: the old steps name parts the repair replaced.
    const tail = result.repaired?.length ? undefined : await driver.getHistory(id);
    return { doc: result.doc, projectId: id, tail, viewState: meta?.viewState, ...(result.repaired?.length ? { repaired: result.repaired } : {}) };
  };
  if (overrideId) {
    const fromOverride = await tryLoad(overrideId);
    if (fromOverride && "doc" in fromOverride) {
      await driver.putSetting("lastProjectId", overrideId); // a reload without the param reopens it
      return fromOverride;
    }
    // not local (or unreadable) → normal startup below; boot() attempts an Eden pull
  }
  const fromLast = await tryLoad(lastId);
  if (fromLast && "doc" in fromLast) return fromLast;
  const loadError = fromLast && "error" in fromLast ? fromLast.error : undefined;
  const newest = (await driver.listMeta()).sort((a, b) => b.modifiedAt.localeCompare(a.modifiedAt))[0];
  const fromNewest = newest && newest.id !== lastId ? await tryLoad(newest.id) : null;
  if (fromNewest && "doc" in fromNewest) return { ...fromNewest, loadError };
  const doc = createEmptyDoc("Untitled");
  return { doc, projectId: doc.id, loadError };
}
